import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DropdownSearchComponent, InputComponent } from '@libs/ui-lib';
import { EXTERNAL_LINKS } from '@libs/utils';
import { statesDropdownOptions } from '@libs/site-setup';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { BaaSignupFlowService } from '../baa-sign-up-flow.service';

type AgreementLinkToken = 'input1' | 'input2' | 'input3';

interface AgreementPart {
  text?: string;
  token?: AgreementLinkToken;
}

@Component({
  selector: 'lib-baa-signup-practice-details',
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, InputComponent, DropdownSearchComponent],
  templateUrl: './baa-sign-up-practice-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaaSignupPracticeDetailsComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  protected readonly service = inject(BaaSignupFlowService);
  protected readonly isSubmitted = signal(false);
  protected readonly statesDropdownOptions = statesDropdownOptions;
  protected readonly termsOfServiceUrl = EXTERNAL_LINKS.FFD_NOTES_TERMS;
  protected readonly baaUrl = EXTERNAL_LINKS.FFD_NOTES_BAA;
  protected readonly privacyPolicyUrl = EXTERNAL_LINKS.FFD_PRIVACY_POLICY;

  // Split the single translated sentence on the {{tos}}/{{privacy}}/{{baa}} tokens so translators
  // keep full control of word order; each token is rendered as its own <ng-template> anchor.
  protected readonly agreementParts = toSignal(
    this.translate.stream('baa_signup_practice_details_agreement').pipe(map((skeleton: string) => this.buildAgreementParts(skeleton))),
    { initialValue: [] as AgreementPart[] },
  );

  protected readonly form = new FormGroup({
    legalName: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    address: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    city: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    state: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    zip: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    signerFullName: new FormControl<string>({ value: '', disabled: true }, { nonNullable: true }),
  });

  public ngOnInit(): void {
    const signerName = this.service.signerNameSignal() ?? '';
    this.form.controls.signerFullName.setValue(signerName);
  }

  protected onSubmit(): void {
    this.isSubmitted.set(true);
    if (this.form.invalid) return;
    const { legalName, address, city, state, zip } = this.form.getRawValue();
    this.service.submitPracticeDetails({ legalName, address, city, state, zip });
  }

  private buildAgreementParts(skeleton: string): AgreementPart[] {
    return skeleton
      .split(/({{input1}}|{{input2}}|{{input3}})/)
      .filter((segment) => segment.length > 0)
      .map((segment) => {
        const token = segment.match(/^{{(input1|input2|input3)}}$/)?.[1] as AgreementLinkToken | undefined;
        return token ? { token } : { text: segment };
      });
  }
}
